import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from "@/components/ui/use-toast";
import SharedHeader from './layout/SharedHeader';
import { useUserSettings } from '../hooks/useUserSettings';

const Settings = () => {
  const { user } = useAuth();
  const { settings, loading, error, updateSettings } = useUserSettings(user?.uid);
  const { toast } = useToast();

  const handleNotificationChange = (type) => {
    updateSettings({
      ...settings,
      notifications: {
        ...settings.notifications,
        [type]: !settings.notifications[type]
      }
    });
  };

  const handleThemeChange = (newTheme) => {
    updateSettings({
      ...settings,
      theme: newTheme
    });
  };

  const handleSaveSettings = async () => {
    const success = await updateSettings(settings);
    
    if (success) {
      toast({
        title: "Settings saved",
        description: "Your preferences have been updated.",
      });
    } else {
      toast({
        variant: "destructive",
        title: "Error",
        description: error || "Failed to save settings",
      });
    }
  };

  if (!user) {
    return <div>Please log in to access settings</div>;
  }

  return (
    <div className="min-h-screen bg-neutral-white">
      <SharedHeader />
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
            <div className="p-6">
              <h2 className="text-lg font-medium text-gray-900">Notifications</h2>
              <div className="mt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Email notifications</h3>
                    <p className="text-sm text-gray-500">Receive email updates about your account</p>
                  </div>
                  <button
                    onClick={() => handleNotificationChange('email')}
                    className={`${
                      settings.notifications.email ? 'bg-blue-600' : 'bg-gray-200'
                    } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                  >
                    <span className="sr-only">Enable email notifications</span>
                    <span
                      className={`${
                        settings.notifications.email ? 'translate-x-5' : 'translate-x-0'
                      } pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Push notifications</h3>
                    <p className="text-sm text-gray-500">Receive push notifications in your browser</p>
                  </div>
                  <button
                    onClick={() => handleNotificationChange('push')}
                    className={`${
                      settings.notifications.push ? 'bg-blue-600' : 'bg-gray-200'
                    } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                  >
                    <span className="sr-only">Enable push notifications</span>
                    <span
                      className={`${
                        settings.notifications.push ? 'translate-x-5' : 'translate-x-0'
                      } pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
                    />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              <h2 className="text-lg font-medium text-gray-900">Appearance</h2>
              <div className="mt-6">
                <fieldset className="mt-2">
                  <div className="space-y-4">
                    {['light', 'dark', 'system'].map((mode) => (
                      <div key={mode} className="flex items-center">
                        <input
                          id={mode}
                          name="theme"
                          type="radio"
                          checked={settings.theme === mode}
                          onChange={() => handleThemeChange(mode)}
                          className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor={mode} className="ml-3 block text-sm font-medium text-gray-700 capitalize">
                          {mode}
                        </label>
                      </div>
                    ))}
                  </div>
                </fieldset>
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-md">
              {error}
            </div>
          )}

          <div className="mt-6">
            <button
              onClick={handleSaveSettings}
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;